/**
 * Xuất Excel tab "Lịch sử khám" — mô phỏng nút "Xuất danh sách" của SalePro admin: MỘT nút, xuất
 * toàn bộ kết quả khớp bộ lọc đang áp (quyết định 03/11/2026: không có lựa chọn "trang hiện tại").
 *
 * Cùng cách làm với trang Bệnh nhân (`PatientListPage.onExport`): tệp dựng ở trình duyệt bằng
 * SheetJS từ CHÍNH API danh sách đang hiển thị, nên bộ lọc (từ khoá, trạng thái, bác sĩ, khoảng
 * ngày) áp lên tệp y như lên bảng và không cần một endpoint xuất riêng. Tệp đi qua từng
 * trang 500 dòng — mỗi trang vẫn là một lần đọc `@PhiAccess` ở server, nên vết kiểm toán PHI
 * không khác gì người dùng tự bấm qua từng trang.
 */
import * as XLSX from "xlsx";
import { formatDateTime, formatPhone, genderLabel } from "../../shared/format";
import type {
  EncounterListFilter,
  EncounterSummary,
} from "../../shared/types/encounter";
import type { PageResponse } from "../../shared/types/patient";
import { STATUS_LABEL } from "./encounterStatus";

/** Kích thước trang khi gom "tất cả" — trùng với trang Bệnh nhân. */
export const EXPORT_PAGE_SIZE = 500;

/** Thứ tự cột của tệp — đây là hợp đồng với người mở tệp, đổi là đổi ý nghĩa cột. */
export const ENCOUNTER_EXPORT_HEADERS = [
  "STT",
  "Số thứ tự",
  "Mã BN",
  "Bệnh nhân",
  "Số điện thoại",
  "Giới tính",
  "Tuổi",
  "Khám lúc",
  "Lý do khám",
  "Bác sĩ",
  "Chẩn đoán",
  "Đơn thuốc",
  "Trạng thái",
  "Hoàn tất lúc",
] as const;

export type EncounterExportRow = (string | number)[];

/**
 * Số thứ tự tiếp đón in như SalePro (`STT0213`): HIS lưu số nguyên, tiền tố + đệm 4 chữ số là định
 * dạng hiển thị của SalePro mà migration đã bóc ra (`migrate_prod.py`: lấy phần số cuối của
 * `queue_number`). Không có số → ô trống.
 */
export function formatRegistrationNo(n: number | null | undefined): string {
  return n == null ? "" : `STT${String(n).padStart(4, "0")}`;
}

/**
 * Một dòng tệp từ một dòng danh sách; `index` là vị trí trong tệp (cột STT = số dòng, như SalePro).
 * Ô trống là "" (không phải "—") để lọc/sort trong Excel.
 */
export function encounterExportRow(e: EncounterSummary, index: number): EncounterExportRow {
  return [
    index + 1,
    formatRegistrationNo(e.registrationNo),
    e.patientCode ?? "",
    e.patientName,
    e.patientPhone ? formatPhone(e.patientPhone) : "",
    e.patientGender ? genderLabel[e.patientGender] : "",
    e.patientAge ?? "",
    formatDateTime(e.startedAt),
    e.chiefComplaint ?? "",
    e.doctorName,
    e.diagnosisSummary ?? "",
    e.hasPrescription ? "Có" : "Không",
    STATUS_LABEL[e.status],
    e.completedAt ? formatDateTime(e.completedAt) : "",
  ];
}

/** `2026-10-01` → `01-10-2026` (dạng ngày trong tên tệp của SalePro). */
function fileDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}-${m}-${y}`;
}

/**
 * Tên tệp theo quy ước xuất của SalePro admin (`Danh sách yêu cầu 01-10-2026 đến 31-10-2026.xlsx`):
 * tên danh sách + khoảng ngày `dd-MM-yyyy đến dd-MM-yyyy`; một ngày thì chỉ ghi ngày đó. Khoảng
 * ngày chỉ xuất hiện khi có lọc — một tệp không mang ngày là một tệp không lọc theo ngày, chứ
 * không phải tệp "mọi ngày từ trước tới nay" được đặt tên nhầm.
 */
export function encounterExportFileName(
  fromDate: string | null | undefined,
  toDate: string | null | undefined
): string {
  const name = "Lịch sử khám";
  let range = "";
  if (fromDate && toDate) {
    range = fromDate === toDate ? fileDate(fromDate) : `${fileDate(fromDate)} đến ${fileDate(toDate)}`;
  } else if (fromDate) {
    range = `từ ${fileDate(fromDate)}`;
  } else if (toDate) {
    range = `đến ${fileDate(toDate)}`;
  }
  return `${range ? `${name} ${range}` : name}.xlsx`;
}

/**
 * Gom MỌI dòng khớp bộ lọc, trang này nối trang kia, cùng thứ tự với bảng (mới nhất trước).
 * `list` tiêm vào để test không cần mạng. Dừng khi hết trang hoặc server trả trang rỗng — trang
 * rỗng giữa chừng (dữ liệu bị xoá trong lúc xuất) không được phép thành vòng lặp vô tận.
 */
export async function fetchAllEncounters(
  filter: EncounterListFilter,
  list: (
    filter: EncounterListFilter,
    pageable: { page: number; size: number; sort: string }
  ) => Promise<PageResponse<EncounterSummary>>
): Promise<EncounterSummary[]> {
  const rows: EncounterSummary[] = [];
  let page = 0;
  for (;;) {
    const data = await list(filter, { page, size: EXPORT_PAGE_SIZE, sort: "startedAt,desc" });
    rows.push(...data.content);
    if (data.content.length === 0 || page + 1 >= (data.totalPages ?? 1)) break;
    page += 1;
  }
  return rows;
}

/** Dựng workbook một sheet và tải xuống. Tách riêng để test ghim được nội dung mà không cần tệp. */
export function buildEncounterWorkbook(rows: EncounterSummary[]): XLSX.WorkBook {
  const ws = XLSX.utils.aoa_to_sheet([
    [...ENCOUNTER_EXPORT_HEADERS],
    ...rows.map(encounterExportRow),
  ]);
  ws["!cols"] = [6, 10, 12, 28, 14, 10, 6, 18, 36, 24, 36, 10, 14, 18].map((wch) => ({ wch }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Lịch sử khám");
  return wb;
}

export function downloadEncountersXlsx(rows: EncounterSummary[], filename: string): void {
  XLSX.writeFile(buildEncounterWorkbook(rows), filename);
}
